import { TrashIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import Button from 'components/inputs/button';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import {
  RegionalHeatingFactorRowDraft,
  validateRegionalHeatingFactors
} from 'utils/regional-heating-factors-validation';
import { errorModal, successModal } from 'utils/ui';

// Shipped defaults — mirrors REGIONAL_HEATING_FACTOR_BY_DESIGN_TEMP /
// _BELOW_ZERO / _FALLBACK in the API and the migration seed. Used only to
// hydrate the form when the setting is absent (older API deploy without the
// seed migration); Save takes effect only once the migration has run,
// because the settings PATCH endpoint is UPDATE-only.
const DEFAULT_REGIONAL_HEATING_FACTORS = {
  buckets: [
    { minTempF: 40, maxTempF: null, factor: 1 },
    { minTempF: 30, maxTempF: 40, factor: 1.2 },
    { minTempF: 20, maxTempF: 30, factor: 1.5 },
    { minTempF: 10, maxTempF: 20, factor: 1.8 },
    { minTempF: 0, maxTempF: 10, factor: 2.1 },
    { minTempF: null, maxTempF: 0, factor: 2.4 }
  ] as { minTempF: number | null; maxTempF: number | null; factor: number }[],
  fallbackFactor: 1.5
};

// Warmest bucket first (blank/unbounded min last), matching how the ladder
// reads in the API.
const sortRows = (
  rows: RegionalHeatingFactorRowDraft[]
): RegionalHeatingFactorRowDraft[] =>
  [...rows].sort(
    (a, b) => (b.minTempF ?? -Infinity) - (a.minTempF ?? -Infinity)
  );

// Self-contained 'Regional Heating Factors' settings block (AHA-6796): the
// heating requirement on the tonnage-estimate coverage path is the cooling
// requirement times the factor of the bucket containing the design heating
// 99% dry-bulb. Self-contained so the upcoming admin-page restructure
// (db7b9e59) can slot it in unchanged.
export default function RegionalHeatingFactorsSettings() {
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.admin.settings);
  const [rows, setRows] = useState<RegionalHeatingFactorRowDraft[]>([]);
  const [fallbackFactor, setFallbackFactor] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (!settings) {
      return;
    }
    const value =
      settings.regionalHeatingFactors ?? DEFAULT_REGIONAL_HEATING_FACTORS;
    setRows(sortRows(value.buckets.map((bucket) => ({ ...bucket }))));
    setFallbackFactor(value.fallbackFactor);
  }, [settings]);

  const errors = validateRegionalHeatingFactors(rows, fallbackFactor);
  const hasErrors = Boolean(
    errors.rows || errors.fallbackFactor || errors.form
  );

  const handleRowChange = (
    index: number,
    key: 'minTempF' | 'maxTempF' | 'factor',
    value: number | undefined
  ) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        // Blank min/max inputs mean "no limit" on that side.
        [key]: key === 'factor' ? value : value ?? null
      };
      return next;
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      { minTempF: null, maxTempF: null, factor: undefined }
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (hasErrors) {
      return;
    }
    const buckets = sortRows(rows).map((row) => ({
      minTempF: row.minTempF,
      maxTempF: row.maxTempF,
      factor: Number(row.factor)
    }));
    try {
      await axios.patch('settings', [
        {
          key: 'regionalHeatingFactors',
          value: JSON.stringify({ buckets, fallbackFactor })
        }
      ]);
      dispatch(fetchDefaultAdminData());
      successModal(`Settings updated.`);
    } catch {
      errorModal('Something went wrong.');
    }
  };

  return (
    <div className='flex flex-col gap-5'>
      <div className='text-xl font-bold text-gray-900'>
        Regional Heating Factors
      </div>
      <div className='text-gray-500'>
        When only a tonnage estimate is available, the heating requirement is
        the cooling requirement times the factor of the bucket containing the
        design heating 99% dry-bulb (°F). Leave a temp blank for &quot;no
        limit&quot; on that side; buckets must cover all temps with no gaps or
        overlaps.
      </div>
      <div className='flex max-w-2xl flex-col gap-3'>
        {rows.map((row, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className='flex flex-col gap-1'>
            <div className='flex items-start gap-3 rounded-lg bg-white p-3'>
              <div className='flex flex-col gap-1'>
                <FormInput
                  type='number'
                  label='Min temp (°F)'
                  value={row.minTempF ?? undefined}
                  onChange={(value) =>
                    handleRowChange(index, 'minTempF', value)
                  }
                  placeholder='No limit'
                  step={1}
                />
                {errors.rows?.[index]?.minTempF && (
                  <div className='text-sm text-red-500'>
                    {errors.rows[index].minTempF}
                  </div>
                )}
              </div>
              <div className='flex flex-col gap-1'>
                <FormInput
                  type='number'
                  label='Max temp (°F)'
                  value={row.maxTempF ?? undefined}
                  onChange={(value) =>
                    handleRowChange(index, 'maxTempF', value)
                  }
                  placeholder='No limit'
                  step={1}
                />
                {errors.rows?.[index]?.maxTempF && (
                  <div className='text-sm text-red-500'>
                    {errors.rows[index].maxTempF}
                  </div>
                )}
              </div>
              <div className='flex flex-col gap-1'>
                <FormInput
                  type='number'
                  label='Factor'
                  value={row.factor}
                  onChange={(value) => handleRowChange(index, 'factor', value)}
                  step={0.1}
                  min={0}
                  required
                />
                {errors.rows?.[index]?.factor && (
                  <div className='text-sm text-red-500'>
                    {errors.rows[index].factor}
                  </div>
                )}
              </div>
              <div className='flex-shrink pt-9'>
                <TrashIcon
                  aria-label={`Remove bucket ${index + 1}`}
                  role='button'
                  className='h-5 w-5 shrink-0 cursor-pointer text-gray-500 hover:text-gray-700'
                  onClick={() => handleRemoveRow(index)}
                />
              </div>
            </div>
          </div>
        ))}
        <div>
          <Button text='Add Bucket' color='white' onClick={handleAddRow} />
        </div>
        <div className='flex max-w-md flex-col gap-1'>
          <FormInput
            type='number'
            label='Fallback factor'
            subLabel='Used when no design temp is resolvable'
            value={fallbackFactor}
            onChange={(value) => setFallbackFactor(value)}
            step={0.1}
            min={0}
            required
          />
          {errors.fallbackFactor && (
            <div className='text-sm text-red-500'>{errors.fallbackFactor}</div>
          )}
        </div>
        {errors.form?.map((message) => (
          <div key={message} className='text-sm text-red-500'>
            {message}
          </div>
        ))}
      </div>
      <div>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={hasErrors}
        />
      </div>
    </div>
  );
}
